export interface BlankingResult {
  originalWords: Array<{ index: number; word: string }>;
  blankedText: string;
}

export interface BlankingOptions {
  minWordLength?: number;
  blankPercentage?: number;
  randomSeed?: number;
  mode?: 'random' | 'manual';
  selectedIndices?: number[];
}

export function createClozeTest(
  inputText: string,
  options: BlankingOptions = {}
): BlankingResult {
  const {
    minWordLength = 3,
    blankPercentage = 0.15,
    randomSeed,
    mode = 'random',
    selectedIndices: manualSelectedIndices
  } = options;

  if (!inputText.trim()) {
    throw new Error('Input text cannot be empty');
  }

  // Split on all punctuation marks and spaces
  const words = inputText.trim().split(/([?!,;:.\s]+)/);

  let selectedIndices: number[] = [];

  if (mode === 'manual' && manualSelectedIndices && manualSelectedIndices.length > 0) {
    // Use manually selected indices
    selectedIndices = [...manualSelectedIndices];
  } else {
    // Use random selection (default behavior)
    const availableIndices = words
      .map((word, index) => ({ word, index }))
      .filter(({ word }) => ! /^[\s,;.:!?]+$/.test(word))
      .filter(({ word }) => word.length > minWordLength)
      .map(({ index }) => index);

    const totalWords = availableIndices.length;
    const numBlanks = Math.max(1, Math.ceil(totalWords * blankPercentage));

    if (availableIndices.length === 0) {
      throw new Error(`No words found longer than ${minWordLength} characters`);
    }

    // Create a seeded random function if seed is provided
    let random = Math.random;
    if (randomSeed !== undefined) {
      random = createSeededRandom(randomSeed);
    }

    const shuffled = [...availableIndices];

    // Fisher-Yates shuffle
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    selectedIndices.push(...shuffled.slice(0, Math.min(numBlanks, shuffled.length)));
  }

  selectedIndices.sort((a, b) => a - b);

  const blankedWords = words.map((word, index) => {
    if (selectedIndices.includes(index)) {
      return `____${index}____`;
    }
    return word;
  });

  const originalWords = selectedIndices.map(index => ({
    index,
    word: words[index]
  }));

  const blankedText = blankedWords.join('');

  return {
    originalWords,
    blankedText
  };
}

function createSeededRandom(seed: number): () => number {
  let state = seed;
  return function() {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}